import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, subMonths, differenceInMonths, differenceInYears } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getIsoWeekNumber, getIsoWeek, getCalendarQuarter, getIsoMonth } from '../utils/isoDates';

const DateRangeSelector = ({
  startDate,
  endDate,
  onDatesChange,
  onPeriodTypeResolved,
  maxDateRange = { value: 3, unit: 'months' },
  includeFuture = false,
  relative = true
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(startDate);
  const [customEndDate, setCustomEndDate] = useState(endDate);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const dropdownRef = useRef(null);
  const customRangeRef = useRef(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [rangeError, setRangeError] = useState(null);
  // Keep the latest callback in a ref so the preset-matching effect can report
  // the resolved period type without re-running on every parent render.
  const onPeriodTypeResolvedRef = useRef(onPeriodTypeResolved);
  onPeriodTypeResolvedRef.current = onPeriodTypeResolved;
  // Définir les plages relatives
  const presets = useMemo(() => {
    if (relative) {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const isFirstSemester = currentMonth < 6;

      // Define quarters data structure
      const quarterDefs = [
        {
          id: 'q4lastyear',
          label: `Q4 (${currentYear - 1})`,
          type: 'quarter',
          value: `${currentYear - 1}-Q4`,
          year: currentYear - 1,
          startMonth: 9,
          endMonth: 11,
          minCurrentMonth: 0
        },
        {
          id: 'q1',
          label: `Q1 (${currentYear})`,
          type: 'quarter',
          value: `${currentYear}-Q1`,
          year: currentYear,
          startMonth: 0,
          endMonth: 2,
          minCurrentMonth: 0
        },
        {
          id: 'q2',
          label: `Q2 (${currentYear})`,
          type: 'quarter',
          value: `${currentYear}-Q2`,
          year: currentYear,
          startMonth: 3,
          endMonth: 5,
          minCurrentMonth: 3
        },
        {
          id: 'q3',
          label: `Q3 (${currentYear})`,
          type: 'quarter',
          value: `${currentYear}-Q3`,
          year: currentYear,
          startMonth: 6,
          endMonth: 8,
          minCurrentMonth: 6
        },
        {
          id: 'q4',
          label: `Q4 (${currentYear})`,
          type: 'quarter',
          value: `${currentYear}-Q4`,
          year: currentYear,
          startMonth: 9,
          endMonth: 11,
          minCurrentMonth: 9
        }
      ];

      // Définition des semestres
      const semesterDefs = [];

      // Seulement ajouter les semestres si maxDateRange >= 6 mois
      if (maxDateRange.value >= 6 || (maxDateRange.unit === 'years' && maxDateRange.value >= 0.5)) {
        // Ajouter le semestre en cours
        semesterDefs.push({
          id: isFirstSemester ? 's1' : 's2',
          label: `S${isFirstSemester ? 1 : 2} (${currentYear})`,
          type: 'semester',
          value: `${currentYear}-S${isFirstSemester ? 1 : 2}`,
          getRange: () => {
            const startMonth = isFirstSemester ? 0 : 6;
            const endMonth = isFirstSemester ? 5 : 11;
            return {
              start: new Date(currentYear, startMonth, 1),
              end: new Date(currentYear, endMonth + 1, 0)
            };
          }
        });

        // Ajouter le semestre précédent
        const prevSemesterIsFirstSemester = !isFirstSemester;
        const prevSemesterYear = isFirstSemester ? currentYear - 1 : currentYear;
        semesterDefs.push({
          id: prevSemesterIsFirstSemester ? 'prev_s1' : 'prev_s2',
          label: `S${prevSemesterIsFirstSemester ? 1 : 2} (${prevSemesterYear})`,
          type: 'semester',
          value: `${prevSemesterYear}-S${prevSemesterIsFirstSemester ? 1 : 2}`,
          getRange: () => {
            const startMonth = prevSemesterIsFirstSemester ? 0 : 6;
            const endMonth = prevSemesterIsFirstSemester ? 5 : 11;
            return {
              start: new Date(prevSemesterYear, startMonth, 1),
              end: new Date(prevSemesterYear, endMonth + 1, 0)
            };
          }
        });
      }

      // Définition des années
      const yearDefs = [];

      // Seulement ajouter les années si maxDateRange >= 12 mois
      if (maxDateRange.value >= 12 || (maxDateRange.unit === 'years' && maxDateRange.value >= 1)) {
        // Année en cours
        yearDefs.push({
          id: 'currentYear',
          label: `Année ${currentYear}`,
          type: 'year',
          value: `${currentYear}`,
          getRange: () => ({
            start: new Date(currentYear, 0, 1),
            end: new Date(currentYear, 11, 31, 23, 59, 59, 999)
          })
        });

        // Année précédente
        yearDefs.push({
          id: 'prevYear',
          label: `Année ${currentYear - 1}`,
          type: 'year',
          value: `${currentYear - 1}`,
          getRange: () => ({
            start: new Date(currentYear - 1, 0, 1),
            end: new Date(currentYear - 1, 11, 31, 23, 59, 59, 999)
          })
        });
      }

      // Filter and map quarters based on current month
      const availableQuarters = quarterDefs
        .filter(q => currentMonth >= q.minCurrentMonth)
        .map(q => ({
          id: q.id,
          label: q.label,
          getRange: () => ({
            start: new Date(q.year, q.startMonth, 1),
            end: new Date(q.year, q.endMonth + 1, 0) // day 0 = last day of endMonth (Q1 used to end March 30)
          })
        }));

      return [
        {
          id: 'custom',
          label: 'Autre mois',
          type: 'custom',
          getRange: () => ({ start: customStartDate, end: customEndDate }),
        },
        {
          id: 'currentWeek',
          label: 'Semaine en cours',
          type: 'week',
          value: getIsoWeekNumber(new Date()).fullWeekString,
          getRange: () => {
            const now = new Date();
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            return { start, end };
          },
        },
        {
          id: 'lastWeek',
          label: 'Semaine précédente',
          type: 'week',
          value: getIsoWeekNumber(new Date(now.getFullYear(), now.getMonth() - 1, now.getDate() - 7)).fullWeekString,
          getRange: () => {
            const now = new Date();
            const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate() - 7);
            const end = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate() - 1);
            return { start, end };
          },
        },
        {
          id: 'currentMonth',
          label: 'Mois en cours',
          type: 'month',
          value: getIsoMonth(new Date()).fullMonthString,
          getRange: () => {
            const now = new Date();
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            return { start, end };
          },
        },
        {
          id: 'lastMonth',
          label: 'Mois précédent',
          type: 'month',
          value: getIsoMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1)).fullMonthString,
          getRange: () => {
            const now = new Date();
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            return { start, end };
          },
        },
        {
          id: 'last1m',
          label: 'Dernier mois',
          type: 'month',
          value: getIsoMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1)).fullMonthString,
          getRange: () => ({ start: subMonths(new Date(), 1), end: new Date() }),
        },
        {
          id: 'last3m',
          label: '3 derniers mois',
          getRange: () => ({ start: subMonths(new Date(), 3), end: new Date() }),
        },
        ...availableQuarters,
        ...semesterDefs, // Ajouter les semestres si disponibles
        ...yearDefs,     // Ajouter les années si disponibles
      ];
    }

    // Mode non-relatif - Adding ISO weeks, calendar quarters and calendar months
    const now = new Date();
    const { year: currentYear, weekNumber: currentWeekNumber } = getIsoWeekNumber(now);

    // Get current month info
    const currentMonth = now.getMonth();
    const currentMonthName = format(now, 'MMMM', { locale: fr });

    // Get previous month info
    let prevMonth = currentMonth - 1;
    let prevMonthYear = currentYear;
    if (prevMonth < 0) {
      prevMonth = 11; // December
      prevMonthYear = currentYear - 1;
    }
    const prevMonthDate = new Date(prevMonthYear, prevMonth, 1);
    const prevMonthName = format(prevMonthDate, 'MMMM', { locale: fr });

    // Get previous previous month info
    let prevPrevMonth = prevMonth - 1;
    let prevPrevMonthYear = prevMonthYear;
    if (prevPrevMonth < 0) {
      prevPrevMonth = 11; // December
      prevPrevMonthYear = prevMonthYear - 1;
    }
    const prevPrevMonthDate = new Date(prevPrevMonthYear, prevPrevMonth, 1);
    const prevPrevMonthName = format(prevPrevMonthDate, 'MMMM', { locale: fr });

    // Calculate previous week info with year boundary check
    let prevWeekNumber = currentWeekNumber - 1;
    let prevWeekYear = currentYear;
    if (prevWeekNumber < 1) {
      prevWeekNumber = 52; // Approximate, actual last week could be 52 or 53
      prevWeekYear = currentYear - 1;
    }

    // Calculate previous previous week info
    let prevPrevWeekNumber = prevWeekNumber - 1;
    let prevPrevWeekYear = prevWeekYear;
    if (prevPrevWeekNumber < 1) {
      prevPrevWeekNumber = 52; // Approximate, actual last week could be 52 or 53
      prevPrevWeekYear = prevWeekYear - 1;
    }

    // Get current quarter (1-4)
    const currentQuarter = Math.floor((now.getMonth() / 3) + 1);

    // Calculate previous quarter with year boundary check
    let prevQuarter = currentQuarter - 1;
    let prevQuarterYear = currentYear;
    if (prevQuarter < 1) {
      prevQuarter = 4;
      prevQuarterYear = currentYear - 1;
    }

    // Définir les options de semestres et années pour le mode non-relatif
    const additionalOptions = [];

    // Ajouter les semestres si maxDateRange >= 6 mois
    if (maxDateRange.value >= 6 || (maxDateRange.unit === 'years' && maxDateRange.value >= 0.5)) {
      const currentSemester = Math.floor(currentMonth / 6) + 1; // 1 ou 2
      const currentSemesterStartMonth = (currentSemester - 1) * 6; // 0 ou 6
      const currentSemesterEndMonth = currentSemesterStartMonth + 5; // 5 ou 11

      // Semestre actuel
      additionalOptions.push({
        id: `currentSemester`,
        label: `Semestre ${currentSemester} • `,
        type: 'semester',
        value: `${currentYear}-S${currentSemester}`,
        getRange: () => {
          const start = new Date(currentYear, currentSemesterStartMonth, 1);
          const end = new Date(currentYear, currentSemesterEndMonth + 1, 0); // Dernier jour du dernier mois
          return { start, end };
        }
      });

      // Semestre précédent
      const prevSemester = currentSemester === 1 ? 2 : 1;
      const prevSemesterYear = currentSemester === 1 ? currentYear - 1 : currentYear;
      const prevSemesterStartMonth = (prevSemester - 1) * 6;
      const prevSemesterEndMonth = prevSemesterStartMonth + 5;

      additionalOptions.push({
        id: `prevSemester`,
        label: `Semestre ${prevSemester} ${prevSemesterYear === currentYear ? '' : `(${prevSemesterYear})`}`,
        type: 'semester',
        value: `${prevSemesterYear}-S${prevSemester}`,
        getRange: () => {
          const start = new Date(prevSemesterYear, prevSemesterStartMonth, 1);
          const end = new Date(prevSemesterYear, prevSemesterEndMonth + 1, 0); // Dernier jour du dernier mois
          return { start, end };
        }
      });
    }

    // Ajouter les années si maxDateRange >= 12 mois
    if (maxDateRange.value >= 12 || (maxDateRange.unit === 'years' && maxDateRange.value >= 1)) {
      // Année courante
      additionalOptions.push({
        id: 'currentYear',
        label: `Année ${currentYear} • `,
        type: 'year',
        value: `${currentYear}`,
        getRange: () => {
          const start = new Date(currentYear, 0, 1);
          const end = new Date(currentYear, 11, 31, 23, 59, 59, 999);
          return { start, end };
        }
      });

      // Année précédente
      additionalOptions.push({
        id: 'prevYear',
        label: `Année ${currentYear - 1}`,
        type: 'year',
        value: `${currentYear - 1}`,
        getRange: () => {
          const start = new Date(currentYear - 1, 0, 1);
          const end = new Date(currentYear - 1, 11, 31, 23, 59, 59, 999);
          return { start, end };
        }
      });
    }

    const nextWeek = [];
    const nextMonth = [];
    const nextQuarter = [];

    if (includeFuture) {
      nextWeek.push({
        id: 'nextIsoWeek',
        label: `Week ${currentWeekNumber + 1 > 52 ? `01 (${currentYear + 1})` : (currentWeekNumber + 1 < 10 ? '0' : '') + (currentWeekNumber + 1)}`,
        type: 'week',
        value: getIsoWeekNumber(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7)).fullWeekString,
        getRange: () => {
          const { startDate, endDate } = getIsoWeek(currentWeekNumber + 1, currentYear);
          return { start: startDate, end: endDate };
        },
      });

      const nextMonthName = format(new Date(currentYear, currentMonth + 1, 1), 'MMMM', { locale: fr });
      nextMonth.push({
        id: 'nextIsoMonth',
        label: `${nextMonthName.charAt(0).toUpperCase() + nextMonthName.slice(1)}${currentMonth + 1 > 12 ? ` (${currentYear + 1})` : ''}`,
        type: 'month',
        value: getIsoMonth(new Date(currentYear, currentMonth + 1, 1)).fullMonthString,
        getRange: () => {
          const start = new Date(currentYear, currentMonth + 1, 1);
          const end = new Date(currentYear, currentMonth + 2, 0); // Dernier jour du mois
          return { start, end };
        },
      });

      const nextQuarterNumber = currentQuarter + 1 > 4 ? 1 : currentQuarter + 1;
      const nextQuarterYear = currentQuarter + 1 > 4 ? currentYear + 1 : currentYear;
      nextQuarter.push({
        id: 'nextIsoQuarter',
        label: `Q${nextQuarterNumber} ${nextQuarterYear === currentYear ? '' : `(${nextQuarterYear})`}`,
        type: 'quarter',
        value: `${nextQuarterYear}-Q${nextQuarterNumber}`,
        getRange: () => {
          const { startDate, endDate } = getCalendarQuarter(nextQuarterNumber, nextQuarterYear);
          return { start: startDate, end: endDate };
        },
      });
    }

    return [
      // Custom option first
      {
        id: 'custom',
        type: 'custom',
        label: 'Plage personnalisée',
        getRange: () => ({ start: customStartDate, end: customEndDate }),
      },
      // ISO week options
      ...nextWeek,
      {
        id: 'currentIsoWeek',
        label: `Week ${currentWeekNumber < 10 ? '0' : ''}${currentWeekNumber} • `,
        type: 'week',
        value: getIsoWeekNumber(new Date()).fullWeekString,
        getRange: () => {
          const { startDate, endDate } = getIsoWeek(currentWeekNumber, currentYear);
          return { start: startDate, end: endDate };
        },
      },
      {
        id: 'prevIsoWeek',
        label: `Week ${prevWeekNumber < 10 ? '0' : ''}${prevWeekNumber} ${prevWeekYear === currentYear ? '' : `(${prevWeekYear})`}`,
        type: 'week',
        value: getIsoWeekNumber(getIsoWeek(prevWeekNumber, prevWeekYear).startDate).fullWeekString,
        getRange: () => {
          const { startDate, endDate } = getIsoWeek(prevWeekNumber, prevWeekYear);
          return { start: startDate, end: endDate };
        },
      },
      {
        id: 'prevPrevIsoWeek',
        label: `Week ${prevPrevWeekNumber < 10 ? '0' : ''}${prevPrevWeekNumber} ${prevPrevWeekYear === currentYear ? '' : `(${prevPrevWeekYear})`}`,
        type: 'week',
        value: getIsoWeekNumber(getIsoWeek(prevPrevWeekNumber, prevPrevWeekYear).startDate).fullWeekString,
        getRange: () => {
          const { startDate, endDate } = getIsoWeek(prevPrevWeekNumber, prevPrevWeekYear);
          return { start: startDate, end: endDate };
        },
      },
      // Calendar month options
      ...nextMonth,
      {
        id: 'currentMonth',
        label: `${currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1)} • `,
        type: 'month',
        value: getIsoMonth(new Date()).fullMonthString,
        getRange: () => {
          const start = new Date(currentYear, currentMonth, 1);
          const end = new Date(currentYear, currentMonth + 1, 0); // Dernier jour du mois
          return { start, end };
        },
      },
      {
        id: 'prevMonth',
        label: `${prevMonthName.charAt(0).toUpperCase() + prevMonthName.slice(1)} ${prevMonthYear !== currentYear ? prevMonthYear : ''}`,
        type: 'month',
        value: getIsoMonth(new Date(prevMonthYear, prevMonth, 1 + 1)).fullMonthString,
        getRange: () => {
          const start = new Date(prevMonthYear, prevMonth, 1);
          const end = new Date(prevMonthYear, prevMonth + 1, 0); // Dernier jour du mois
          return { start, end };
        },
      },
      {
        id: 'prevPrevMonth',
        label: `${prevPrevMonthName.charAt(0).toUpperCase() + prevPrevMonthName.slice(1)} ${prevPrevMonthYear !== currentYear ? prevPrevMonthYear : ''}`,
        type: 'month',
        value: getIsoMonth(new Date(prevPrevMonthYear, prevPrevMonth, 1 + 1)).fullMonthString,
        getRange: () => {
          const start = new Date(prevPrevMonthYear, prevPrevMonth, 1);
          const end = new Date(prevPrevMonthYear, prevPrevMonth + 1, 0); // Dernier jour du mois
          return { start, end };
        },
      },
      // Calendar quarter options
      ...nextQuarter,
      {
        id: 'currentIsoQuarter',
        label: `Q${currentQuarter} • `,
        type: 'quarter',
        value: `${currentYear}-Q${currentQuarter}`,
        getRange: () => {
          const { startDate, endDate } = getCalendarQuarter(currentQuarter, currentYear);
          return { start: startDate, end: endDate };
        },
      },
      {
        id: 'prevIsoQuarter',
        label: `Q${prevQuarter} ${prevQuarterYear === currentYear ? '' : `(${prevQuarterYear})`}`,
        type: 'quarter',
        value: `${prevQuarterYear}-Q${prevQuarter}`,
        getRange: () => {
          const { startDate, endDate } = getCalendarQuarter(prevQuarter, prevQuarterYear);
          return { start: startDate, end: endDate };
        },
      },
      // Nouvelles options de semestres et années
      ...additionalOptions,
    ];
  }, [relative, customStartDate, customEndDate]);

  // Définir l'ordre des presets
  const presetsOrder = ['Custom', 'IsoWeek', 'Month', 'IsoQuarter', 'Semester', 'Year'];

  // Vérifier si un preset est compatible avec le maxDateRange actuel
  const isPresetCompatible = (preset) => {
    if (preset.id === 'custom') return true;

    const { start, end } = preset.getRange();

    if (maxDateRange.unit === 'months') {
      return differenceInMonths(end, start) <= maxDateRange.value;
    } else if (maxDateRange.unit === 'years') {
      return differenceInYears(end, start) <= maxDateRange.value;
    }

    return true;
  };

  // Determine which preset is selected based on current date range
  useEffect(() => {
    if (!startDate || !endDate) {
      setSelectedPreset(null);
      return;
    }

    // Try to match current date range with a preset
    const matchedPreset = presets.find(preset => {
      if (preset.id === 'custom') return false;

      const range = preset.getRange();
      return format(range.start, 'yyyy-MM-dd') === format(startDate, 'yyyy-MM-dd') &&
             format(range.end, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd');
    });

    setSelectedPreset(matchedPreset ? matchedPreset.id : 'custom');

    // Authoritative period-type resolution: whatever the source of the dates
    // (URL deep-link, preset click, custom picker), the matched preset's type —
    // or 'custom' when none matches — is the truth. Reporting it here corrects
    // any stale periodType persisted out-of-band (e.g. in the URL).
    onPeriodTypeResolvedRef.current?.(matchedPreset ? matchedPreset.type : 'custom');

    // Update custom dates if we're in custom mode
    if (!matchedPreset) {
      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
    }
  }, [startDate, endDate, presets]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Pour le dropdown principal
      if (isDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Vérifier si le clic n'est pas dans le dropdown fixe
        const dropdownElement = document.querySelector('.date-range-dropdown');
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setIsDropdownOpen(false);
        }
      }

      // Pour le picker custom
      if (isCustomPickerOpen && customRangeRef.current && !customRangeRef.current.contains(event.target)) {
        // Vérifier si le clic n'est pas dans le bouton principal
        if (!dropdownRef.current || !dropdownRef.current.contains(event.target)) {
          setIsCustomPickerOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, isCustomPickerOpen]);

  // Apply selected preset
  const handleSelectPreset = useCallback((presetId) => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset || !isPresetCompatible(preset)) return;

    if (presetId === 'custom') {
      // Réinitialiser les états temporaires pour une nouvelle sélection
      setTempStartDate(null);
      setTempEndDate(null);
      setIsDropdownOpen(false);
      setIsCustomPickerOpen(true);
    } else {
      const { start, end } = preset.getRange();
      onDatesChange({
        startDate: start,
        endDate: end,
        preset: presetId,
        presetType: preset.type,
        presetValue: preset.value
      });
      setIsDropdownOpen(false);
    }

    setSelectedPreset(presetId);
  }, [onDatesChange, presets, maxDateRange]);

  // Get display text for button
  const getDisplayText = () => {
    if (!startDate || !endDate) return 'Période';

    const preset = presets.find(p => p.id === selectedPreset);
    if (preset && preset.id !== 'custom') {
      return preset.label;
    }

    // For custom range or unrecognized preset
    return `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`;
  };

  // Définir les titres pour chaque catégorie
  const titles = {
    Custom: 'Personnalisé',
    Month: 'Mois',
    IsoWeek: 'Semaines ISO',
    // The key stays 'IsoQuarter': the category grouping matches it against preset
    // ids. The label must not say ISO — the ranges are calendar quarters.
    IsoQuarter: 'Trimestres',
    Semester: 'Semestres',
    Year: 'Années'
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main dropdown button */}
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-200
                  flex items-center justify-between min-h-[40px] text-sm
                  hover:border-gray-300 dark:hover:border-gray-500
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-10
                  shadow-sm transition-all duration-200"
      >
        <span className="text-gray-900 dark:text-gray-200">{getDisplayText()}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform duration-200 text-gray-500 dark:text-gray-400 ${isDropdownOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      {/* Dropdown menu */}
      {isDropdownOpen && (
        <div
          className="date-range-dropdown absolute top-full left-0 mt-1 w-full z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl overflow-hidden overflow-y-auto max-h-[400px]">
          {!relative ? (
            // Mode non-relatif avec sections
            <>
              {presetsOrder.map(category => {
                // Filtrer les presets selon la catégorie
                let categoryPresets = [];
                if (category === 'Custom') {
                  categoryPresets = presets.filter(p => p.id === 'custom');
                } else {
                  categoryPresets = presets.filter(p => p.id.includes(category));
                }

                // Ne pas afficher la section si elle est vide
                if (categoryPresets.length === 0) return null;

                return (
                  <React.Fragment key={category}>
                    {/* En-tête de la section */}
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-600 dark:text-gray-400 font-semibold uppercase tracking-wider">
                      {titles[category]}
                    </div>

                    {/* Éléments de la section */}
                    <div className="px-1 py-1">
                      {categoryPresets.map(preset => {
                        const isCompatible = isPresetCompatible(preset);
                        return (
                          <button
                            key={preset.id}
                            onClick={() => isCompatible && handleSelectPreset(preset.id)}
                            disabled={!isCompatible}
                            className={`w-full text-left px-3 py-2 transition-all duration-150 text-sm rounded-md mx-1
                              ${selectedPreset === preset.id
                                ? 'bg-blue-500 text-white'
                                : isCompatible
                                  ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-200'
                                  : 'text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-gray-900'}`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{preset.label}</span>
                              {!isCompatible && (
                                <span className="text-xs text-gray-500 dark:text-gray-500">
                                  Incompatible
                                </span>
                              )}
                              {preset.id === 'custom' && (
                                <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                                  📅
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </React.Fragment>
                );
              })}
            </>
          ) : (
            // Mode relatif - afficher les presets sans sections
            <div className="px-1 py-1">
              {presets.map((preset) => {
                const isCompatible = isPresetCompatible(preset);
                return (
                  <button
                    key={preset.id}
                    onClick={() => isCompatible && handleSelectPreset(preset.id)}
                    disabled={!isCompatible}
                    className={`w-full text-left px-3 py-2 transition-all duration-150 text-sm rounded-md mx-1 my-0.5
                      ${selectedPreset === preset.id
                        ? 'bg-blue-500 text-white'
                        : isCompatible
                          ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-200'
                          : 'text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-gray-900'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{preset.label}</span>
                      {!isCompatible && (
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          Incompatible
                        </span>
                      )}
                      {preset.id === 'custom' && (
                        <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                          📅
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Custom date range picker modal */}
      {isCustomPickerOpen && (
        <div
          ref={customRangeRef}
          className={`absolute top-full left-0 mt-1 z-[9999] p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl overflow-y-auto max-h-[600px] ${relative ? 'w-[320px]' : 'w-[600px]'}`}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {relative ? 'Sélectionner un mois' : 'Choisir une plage de dates'}
            </h3>
            <button
              onClick={() => {
                setIsCustomPickerOpen(false);
                // Réinitialiser les états temporaires
                setTempStartDate(null);
                setTempEndDate(null);
              }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 text-2xl leading-none transition-colors"
            >
              ×
            </button>
          </div>

          {relative ? (
            <div className="mb-4">
              <DatePicker
                selected={customStartDate}
                onChange={(date) => {
                  const start = new Date(date.getFullYear(), date.getMonth(), 1);
                  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                  setCustomStartDate(start);
                  setCustomEndDate(end);
                  onDatesChange({ startDate: start, endDate: end, preset: 'custom' });
                  setIsCustomPickerOpen(false);
                }}
                dateFormat="MM/yyyy"
                showMonthYearPicker
                locale={fr}
                inline
              />
            </div>
          ) : (
            <>
              <div className="mb-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Sélectionnez la date de début puis la date de fin
                </p>
              </div>

              <div className="mb-4">
                <style>
                  {`
                    /* Force les mois à s'afficher horizontalement */
                    .react-datepicker__month-container {
                      display: inline-block;
                      margin-right: 10px;
                    }

                    /* Supprimer la marge du dernier mois */
                    .react-datepicker__month-container:last-child {
                      margin-right: 0;
                    }

                    /* Règles pour le thème sombre */
                    .dark .react-datepicker {
                      background-color: #1f2937;
                      border-color: #4b5563;
                      color: #e5e7eb;
                    }

                    .dark .react-datepicker__header {
                      background-color: #374151;
                      border-bottom-color: #4b5563;
                    }

                    .dark .react-datepicker__current-month,
                    .dark .react-datepicker-time__header,
                    .dark .react-datepicker-year-header,
                    .dark .react-datepicker__day-name {
                      color: #e5e7eb;
                    }

                    .dark .react-datepicker__day {
                      color: #d1d5db;
                    }

                    .dark .react-datepicker__day:hover {
                      background-color: #4b5563;
                    }

                    .dark .react-datepicker__day--selected,
                    .dark .react-datepicker__day--in-selecting-range,
                    .dark .react-datepicker__day--in-range {
                      background-color: #3b82f6;
                      color: white;
                    }

                    .dark .react-datepicker__day--keyboard-selected {
                      background-color: transparent;
                    }

                    .dark .react-datepicker__day--disabled {
                      color: #6b7280;
                    }

                    /* Fix pour le jour courant dans tous les modes */
                    .react-datepicker__day--today {
                      font-weight: normal;
                      background-color: transparent;
                      border: 1px dotted #aaa;
                    }

                    .dark .react-datepicker__day--today {
                      border-color: #6b7280;
                    }

                    /* Règles pour les dates sélectionnées */
                    .react-datepicker__day--in-range {
                      background-color: rgba(59, 130, 246, 0.2);
                    }

                    .dark .react-datepicker__day--in-range {
                      background-color: rgba(59, 130, 246, 0.3);
                    }

                    .react-datepicker__day--range-start,
                    .react-datepicker__day--range-end {
                      background-color: #3b82f6 !important;
                      color: white !important;
                    }
                  `}
                </style>
                <DatePicker
                  selected={tempStartDate}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setTempStartDate(start);
                    setTempEndDate(end);

                    // Réinitialiser le message d'erreur quand on change la sélection
                    setRangeError(null);
                  }}
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  selectsRange
                  inline
                  locale={fr}
                  monthsShown={2}
                  calendarClassName={document.documentElement.classList.contains('dark') ? 'dark' : ''}
                  todayButton={null}
                />
              </div>

              {/* Message d'erreur */}
              {rangeError && (
                <div className="mb-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {rangeError}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setIsCustomPickerOpen(false);
                    setTempStartDate(null);
                    setTempEndDate(null);
                    setRangeError(null);
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    if (tempStartDate && tempEndDate) {
                      // Vérifier si la plage respecte le maxDateRange
                      let isValid = true;
                      if (maxDateRange.unit === 'months') {
                        isValid = differenceInMonths(tempEndDate, tempStartDate) <= maxDateRange.value;
                      } else if (maxDateRange.unit === 'years') {
                        isValid = differenceInYears(tempEndDate, tempStartDate) <= maxDateRange.value;
                      }

                      if (isValid) {
                        // Appliquer la nouvelle plage
                        setCustomStartDate(tempStartDate);
                        setCustomEndDate(tempEndDate);
                        onDatesChange({
                          startDate: tempStartDate,
                          endDate: tempEndDate,
                          preset: 'custom'
                        });
                        setIsCustomPickerOpen(false);
                        setRangeError(null);
                      } else {
                        // Afficher l'erreur sous le calendrier
                        setRangeError(`La plage sélectionnée dépasse la limite maximale de ${maxDateRange.value} ${maxDateRange.unit === 'months' ? 'mois' : 'années'}.`);
                      }
                    } else if (tempStartDate) {
                      // Si uniquement une date est sélectionnée
                      setCustomStartDate(tempStartDate);
                      setCustomEndDate(tempStartDate);
                      onDatesChange({
                        startDate: tempStartDate,
                        endDate: tempStartDate,
                        preset: 'custom'
                      });
                      setIsCustomPickerOpen(false);
                      setRangeError(null);
                    } else {
                      setRangeError('Veuillez sélectionner au moins une date.');
                    }
                  }}
                  disabled={!tempStartDate}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    tempStartDate
                      ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  }`}
                >
                  Appliquer
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;